import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type {
  BringmeProjectConfigurationDto,
  CopilotPromptConfigurationsDto,
  DicoProjectConfigurationDto,
  HealthDistrictProjectConfigurationDto,
  HomeDnaProjectConfigurationDto,
  HubSpotProjectConfigurationDto,
  ILoqProjectConfigurationDto,
  IntegrationReconciliationDataTypeDto,
  IntegrationReconciliationRequest,
  IqbiProjectConfigurationDto,
  JansenHuybregtsProjectConfigurationDto,
  JimProjectConfigurationDto,
  MyPupProjectConfigurationDto,
  OfficeRndProjectConfigurationDto,
  OutlookProjectConfigurationDto,
  RemsProjectConfigurationRequest,
  SwoogoProjectConfigurationRequest,
  WunderbricksProjectConfigurationDto,
  ZiezodanProjectConfigurationDto,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useEditRemsConfiguration = (projectIdOverride?: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeProjectId = useProjectId();

  return useMutation({
    mutationFn: (data: RemsProjectConfigurationRequest) =>
      api.putIntegrationsRemsV1(
        data,
        projectIdOverride ? { headers: { [ProjectHeaderName]: projectIdOverride } } : undefined,
      ),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.rems.edit.notification.success"), variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_REMS_CONFIGURATION(projectIdOverride ?? activeProjectId),
        }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("rems") }),
      ]);
    },
    onError() {
      showFlashToast({ message: t("page.integration.rems.edit.notification.failed"), variant: "alert" });
    },
  });
};

const usePreviewRemsReconciliation = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      dataType,
      data,
    }: {
      dataType: IntegrationReconciliationDataTypeDto;
      data: IntegrationReconciliationRequest;
    }) => api.postIntegrationsRemsReconciliationPreviewV1(dataType, data).then((response) => response.data),
    onError() {
      showFlashToast({ message: t("page.integration.rems.reconciliation.preview-failed"), variant: "alert" });
    },
  });
};

const useApplyRemsReconciliation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      dataType,
      data,
    }: {
      dataType: IntegrationReconciliationDataTypeDto;
      data: IntegrationReconciliationRequest;
    }) => api.postIntegrationsRemsReconciliationApplyV1(dataType, data).then((response) => response.data),
    async onSuccess(_result, variables) {
      showFlashToast({ message: t("page.integration.rems.reconciliation.apply-success"), variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_REMS_RECONCILIATION_DATA(projectId, variables.dataType),
        }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_REMS_RECONCILIATION_RUNS(projectId) }),
      ]);
    },
    onError() {
      showFlashToast({ message: t("page.integration.rems.reconciliation.apply-failed"), variant: "alert" });
    },
  });
};

const useEditZiezodanConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ZiezodanProjectConfigurationDto) => api.putIntegrationsZiezodanV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.ziezodan.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.ziezodan.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditHomeDnaConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: HomeDnaProjectConfigurationDto) => api.putIntegrationsHomednaV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.homedna.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.homedna.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditDicoConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: DicoProjectConfigurationDto) => api.putIntegrationsDicoV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.dico.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.dico.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditHealthDistrictConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: HealthDistrictProjectConfigurationDto) => api.putIntegrationsHealthdistrictV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.health-district.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.health-district.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditOutlookConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: OutlookProjectConfigurationDto) => api.putIntegrationsOutlookV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.outlook.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.outlook.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditJimConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: JimProjectConfigurationDto) => api.putIntegrationsJimV1(data),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.jim.edit.notification.success"), variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_JIM_CONFIGURATION(projectId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_JIM_ROOMS(projectId) }),
      ]);
    },
    onError() {
      showFlashToast({ message: t("page.integration.jim.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useTestJimConnection = () => {
  const api = useApi();

  return useMutation({
    mutationFn: () => api.postIntegrationsJimTestV1().then((response) => response.data),
  });
};

const useEditIqbiConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: IqbiProjectConfigurationDto) => api.putIntegrationsIqbiV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.iqbi.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.iqbi.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditMyPupConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: MyPupProjectConfigurationDto) => api.putIntegrationsMypupV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.mypup.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.mypup.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditBringmeConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: BringmeProjectConfigurationDto) => api.putIntegrationsBringmeV1(data),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.bringme.edit.notification.success"), variant: "success" });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_BRINGME_CONFIGURATION(projectId) });
    },
    onError() {
      showFlashToast({ message: t("page.integration.bringme.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditCopilotConfigurations = (projectIdOverride?: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeProjectId = useProjectId();

  return useMutation({
    mutationFn: (data: CopilotPromptConfigurationsDto) =>
      api.putIntegrationsCopilotPromptConfigurationsV1(
        data,
        projectIdOverride ? { headers: { [ProjectHeaderName]: projectIdOverride } } : undefined,
      ),
    async onSuccess() {
      showFlashToast({
        message: t("page.integration.copilot-configurations.edit.notification.success"),
        variant: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_COPILOT_CONFIGURATION(projectIdOverride ?? activeProjectId),
        }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("ai") }),
      ]);
    },
    onError() {
      showFlashToast({
        message: t("page.integration.copilot-configurations.edit.notification.failed"),
        variant: "alert",
      });
    },
  });
};

const useEditSwoogoConfigurations = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: SwoogoProjectConfigurationRequest) => api.putIntegrationsSwoogoV1(data),
    onSuccess() {
      showFlashToast({
        message: t("page.integration.swoogo.edit.notification.success"),
        variant: "success",
      });
    },
    onError() {
      showFlashToast({ message: t("page.integration.swoogo.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditOfficeRndConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: OfficeRndProjectConfigurationDto) => api.putIntegrationsOfficerndV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.office-rnd.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.office-rnd.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditHubSpotConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: HubSpotProjectConfigurationDto) => api.putIntegrationsHubspotV1(data),
    onSuccess() {
      showFlashToast({ message: t("page.integration.hubspot.edit.notification.success"), variant: "success" });
    },
    onError() {
      showFlashToast({ message: t("page.integration.hubspot.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditWunderbricksConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: WunderbricksProjectConfigurationDto) => api.putIntegrationsWunderbricksV1(data),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.wunderbricks.edit.notification.success"), variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_WUNDERBRICKS_CONFIGURATION(projectId) }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_WUNDERBRICKS_TICKET_STATUS_OPTIONS(projectId),
        }),
      ]);
    },
    onError() {
      showFlashToast({ message: t("page.integration.wunderbricks.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditIloqConfiguration = (projectIdOverride?: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeProjectId = useProjectId();

  return useMutation({
    mutationFn: (data: ILoqProjectConfigurationDto) =>
      api.putIntegrationsIloqV1(
        data,
        projectIdOverride ? { headers: { [ProjectHeaderName]: projectIdOverride } } : undefined,
      ),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.iloq.edit.notification.success"), variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_ILOQ_CONFIGURATION(projectIdOverride ?? activeProjectId),
        }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("iloq") }),
      ]);
    },
    onError() {
      showFlashToast({ message: t("page.integration.iloq.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useEditJansenHuybregtsConfiguration = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (data: JansenHuybregtsProjectConfigurationDto) => api.putIntegrationsJansenHuybregtsV1(data),
    async onSuccess() {
      showFlashToast({ message: t("page.integration.jansen-huybregts.edit.notification.success"), variant: "success" });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTEGRATION_JANSEN_HUYBREGTS_CONFIGURATION(projectId),
      });
    },
    onError() {
      showFlashToast({ message: t("page.integration.jansen-huybregts.edit.notification.failed"), variant: "alert" });
    },
  });
};

export const integrationMutations = {
  useEditRemsConfiguration,
  usePreviewRemsReconciliation,
  useApplyRemsReconciliation,
  useEditZiezodanConfiguration,
  useEditHomeDnaConfiguration,
  useEditDicoConfiguration,
  useEditHealthDistrictConfiguration,
  useEditOutlookConfiguration,
  useEditJimConfiguration,
  useTestJimConnection,
  useEditIqbiConfiguration,
  useEditMyPupConfiguration,
  useEditBringmeConfiguration,
  useEditCopilotConfigurations,
  useEditSwoogoConfigurations,
  useEditOfficeRndConfiguration,
  useEditHubSpotConfiguration,
  useEditWunderbricksConfiguration,
  useEditIloqConfiguration,
  useEditJansenHuybregtsConfiguration,
};
